import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Container,
  Paper,
  Box,
  Stepper,
  Step,
  StepLabel,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import { Telegram, WhatsApp, Instagram } from "@mui/icons-material";
import PhoneStep from "./PhoneStep";
import ReceiptStep from "./ReceiptStep";
import DoneStep from "./DoneStep";

const steps = ["تایید شماره", "پرداخت", "ارسال رسید"];

export default function CooperationDetails() {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  const phoneFormik = useFormik({
    initialValues: {
      phone: "",
      verificationCode: "",
    },
    validationSchema: Yup.object({
      phone: Yup.string()
        .matches(/^09[0-9]{9}$/, "شماره موبایل معتبر نیست")
        .required("شماره موبایل الزامی است"),
      verificationCode: Yup.string().when("$isCodeSent", {
        is: true,
        then: (schema) =>
          schema
            .length(5, "کد تایید 5 رقمی است")
            .required("کد تایید الزامی است"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (!isCodeSent) {
        setPhoneNumber(values.phone);
        setIsCodeSent(true);
      } else {
        if (values.verificationCode === "12345") {
          setIsVerified(true);
          setTimeout(() => setActiveStep(1), 300);
        } else {
          phoneFormik.setFieldError("verificationCode", "کد تایید اشتباه است");
        }
      }
    },
    context: { isCodeSent },
  });

  const handleChangePhone = () => {
    setIsCodeSent(false);
    setIsVerified(false);
    phoneFormik.setFieldValue("verificationCode", "");
    phoneFormik.setFieldValue("phone", "");
  };

  const handleResendCode = () => {
    console.log("ارسال مجدد کد به:", phoneNumber);
  };

  const bankInfo = {
    cardNumber: "6037-9975-1234-5678",
    bankName: "بانک ملی ایران",
    cardHolder: "شرکت همکاری‌های تجاری",
    amount: "2,500,000",
    amountText: "دو میلیون و پانصد هزار تومان",
  };

  const socialChannels = [
    {
      name: "تلگرام",
      icon: <Telegram sx={{ fontSize: 28 }} />,
      _id: "@SupportCoop",
      link: "https://t.me/SupportCoop",
      color: "#26A5E4",
      hoverColor: "#1e8bc3",
    },
    {
      name: "واتساپ",
      icon: <WhatsApp sx={{ fontSize: 28 }} />,
      _id: "+989123456789",
      link: "https://wa.me/989123456789",
      color: "#25D366",
      hoverColor: "#1da154",
    },
    {
      name: "اینستاگرام",
      icon: <Instagram sx={{ fontSize: 28 }} />,
      _id: "@CoopSupport",
      link: "https://instagram.com/CoopSupport",
      color: "#E4405F",
      hoverColor: "#c1354d",
    },
  ];

  const handleReceiptSubmit = () => {
    setActiveStep(2);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <PhoneStep
            handleResendCode={handleResendCode}
            handleChangePhone={handleChangePhone}
            phoneNumber={phoneNumber}
            isCodeSent={isCodeSent}
            phoneFormik={phoneFormik}
            key={"phoneStep"}
          />
        );

      case 1:
        return (
          <ReceiptStep
            bankInfo={bankInfo}
            handleReceiptSubmit={handleReceiptSubmit}
            socialChannels={socialChannels}
            key={"ReceiptStep"}
          />
        );

      case 2:
        return <DoneStep phoneNumber={phoneNumber} key={"doneStep"} />;

      default:
        return null;
    }
  };

  return (
    <Container
      maxWidth="xl"
      sx={{ py: 6, display: "flex", alignItems: "center" }}
    >
      <Paper
        sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: 6,
          backdropFilter: "blur(10px)",
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          position: "relative",
          overflow: "hidden",
          width: "100%",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
            animation: "gradient 3s ease infinite",
            "@keyframes gradient": {
              "0%": { backgroundPosition: "0% 50%" },
              "50%": { backgroundPosition: "100% 50%" },
              "100%": { backgroundPosition: "0% 50%" },
            },
          }}
        />
        <Stack sx={{ "& *": { direction: "ltr !important" } }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              mb: 5,
              "& .MuiStepLabel-label": {
                fontSize: "0.85rem",
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      "&.Mui-active": {
                        color: theme.palette.secondary.main,
                      },
                      "&.Mui-completed": {
                        color: theme.palette.success.main,
                      },
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Stack>
        <Stack sx={{ maxWidth: "md", mx: "auto" }}>
          {getStepContent(activeStep)}
        </Stack>
      </Paper>
    </Container>
  );
}
